// The family chart as a vector PDF: cards, lines, and circles as shapes, and text set in Noto
// Sans (embedded, cut down to the letters used), so it stays sharp however far it's zoomed or
// however large it's printed, and its text can be selected and searched as real text.
//
// Noto Sans covers Latin, Greek, Cyrillic, and Vietnamese. A label with a character it lacks
// (Chinese, Japanese, Korean, Arabic, emoji…) is drawn as a very sharp picture instead, with
// an invisible copy of its text over it (the blank font, as in pdf.ts) so it can still be found.
// Profile photos are pictures too, as they are on screen.

import { approxWidth, code4, deflate, flateImage, jpegImage, pdfDate, pdfText, PdfWriter, toUnicodeCMap, winAnsi, type PagePlan, type PdfImage, type Tile } from '../../io/pdf';
import { BLANK_ADVANCE, blankFont, parseFont, type Font } from '../../io/ttf';
import { font as cssFont } from '../views/tableLayout';
import { canvasBlob, type ChartSvg, type Shape, type Style } from './chart';

export interface VectorFonts {
  regular: Font;
  semibold: Font;
}

/** A label drawn as a picture: the picture and the box it fills, in the chart's pixels. */
export interface LabelPicture {
  image: PdfImage;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface VectorAssets {
  fonts: VectorFonts;
  /** Profile photos, square, by their data URL. */
  photos: Map<string, PdfImage>;
  /** Labels the fonts can't set, by their index in the chart's shapes. */
  labels: Map<number, LabelPicture>;
}

/** Text at or past this weight is set in the semibold face. */
const BOLD = 600;
const fontFor = (fonts: VectorFonts, weight: number) => (weight >= BOLD ? fonts.semibold : fonts.regular);

/** The glyph for a character; spacing the font hasn't got (a tab, an ideographic space) is set as a plain space, not a missing-letter box. */
function glyphFor(f: Font, ch: string): number {
  const g = f.glyphOf(ch.codePointAt(0)!);
  return g || !/\s/.test(ch) ? g : f.glyphOf(32);
}

/** Whether a label has a character the font can't draw (so it goes in as a picture). */
export function needsPicture(fonts: VectorFonts, s: Extract<Shape, { kind: 'text' }>): boolean {
  const f = fontFor(fonts, s.weight);
  for (const ch of s.text) if (!glyphFor(f, ch)) return true;
  return false;
}

/** A label's natural width in the font, in the chart's pixels. */
function fontWidth(f: Font, text: string, size: number): number {
  let units = 0;
  for (const ch of text) units += f.advance(glyphFor(f, ch));
  return (units / f.unitsPerEm) * size;
}

/** Where a label's left edge goes and how much it's squeezed (Tz) to fit the room it had on screen. */
function textPlacement(s: Extract<Shape, { kind: 'text' }>, natural: number): { left: number; stretch: number; width: number } {
  // Text is cut to fit by its on-screen width; a font that sets it wider is squeezed to the same room.
  const width = Math.min(natural, s.width);
  const stretch = natural > s.width && natural > 0 ? (s.width / natural) * 100 : 100;
  return { left: s.middle ? s.x - width / 2 : s.x, stretch, width };
}

const n = (v: number) => (Math.abs(v) < 1e-9 ? '0' : Number(v.toFixed(3)).toString());
/** Scale factors (a font unit is about a hundredth of a pixel) need more places than positions. */
const fine = (v: number) => (Math.abs(v) < 1e-12 ? '0' : Number(v.toFixed(6)).toString());

/** "#7c6cf0" (or "#fff") as PDF color numbers; anything else is black. */
function rgb(color: string): string | undefined {
  if (!color || color === 'none') return undefined;
  let m = color.match(/^#([0-9a-f]{6})$/i);
  if (m) {
    const v = parseInt(m[1], 16);
    return `${n((v >> 16) / 255)} ${n(((v >> 8) & 255) / 255)} ${n((v & 255) / 255)}`;
  }
  m = color.match(/^#([0-9a-f]{3})$/i);
  if (m) return [...m[1]].map((c) => n(parseInt(c + c, 16) / 255)).join(' ');
  return '0 0 0';
}

/** Each segment of SVG path data (absolute M, L, H, V, C, Z, as edges.ts writes it), with its points. */
function walkPath(d: string, visit: (op: 'm' | 'l' | 'c' | 'h', pts: number[]) => void) {
  const tokens = d.match(/[MLHVCZ]|-?\d*\.?\d+(?:e-?\d+)?/gi) ?? [];
  let x = 0;
  let y = 0;
  let cmd = '';
  for (let i = 0; i < tokens.length; ) {
    if (/^[A-Z]$/i.test(tokens[i])) cmd = tokens[i++].toUpperCase();
    const num = () => Number(tokens[i++]);
    if (cmd === 'M' || cmd === 'L') {
      x = num();
      y = num();
      visit(cmd === 'M' ? 'm' : 'l', [x, y]);
      if (cmd === 'M') cmd = 'L';
    } else if (cmd === 'H') {
      x = num();
      visit('l', [x, y]);
    } else if (cmd === 'V') {
      y = num();
      visit('l', [x, y]);
    } else if (cmd === 'C') {
      const c = [num(), num(), num(), num(), num(), num()];
      x = c[4];
      y = c[5];
      visit('c', c);
    } else if (cmd === 'Z') {
      visit('h', []);
      cmd = '';
    } else i++;
  }
}

/** SVG path data as PDF path operators, moved by (dx, dy). */
export function pathOps(d: string, dx: number, dy: number): string {
  const out: string[] = [];
  walkPath(d, (op, pts) => out.push([...pts.map((v, i) => n(v + (i % 2 ? dy : dx))), op].join(' ')));
  return out.join(' ');
}

/** A rectangle with rounded corners (r), as a path. */
function roundRect(x: number, y: number, w: number, h: number, r: number): string {
  if (!r) return `${n(x)} ${n(y)} ${n(w)} ${n(h)} re`;
  const k = r * 0.5523;
  return [
    `${n(x + r)} ${n(y)} m`,
    `${n(x + w - r)} ${n(y)} l`,
    `${n(x + w - r + k)} ${n(y)} ${n(x + w)} ${n(y + r - k)} ${n(x + w)} ${n(y + r)} c`,
    `${n(x + w)} ${n(y + h - r)} l`,
    `${n(x + w)} ${n(y + h - r + k)} ${n(x + w - r + k)} ${n(y + h)} ${n(x + w - r)} ${n(y + h)} c`,
    `${n(x + r)} ${n(y + h)} l`,
    `${n(x + r - k)} ${n(y + h)} ${n(x)} ${n(y + h - r + k)} ${n(x)} ${n(y + h - r)} c`,
    `${n(x)} ${n(y + r)} l`,
    `${n(x)} ${n(y + r - k)} ${n(x + r - k)} ${n(y)} ${n(x + r)} ${n(y)} c`,
    'h',
  ].join(' ');
}

function circlePath(cx: number, cy: number, r: number): string {
  return roundRect(cx - r, cy - r, r * 2, r * 2, r);
}

/** Each shape's bounds, in the chart's pixels, to put on a page only what falls on it. */
function bounds(s: Shape, fonts: VectorFonts): [number, number, number, number] {
  switch (s.kind) {
    case 'rect':
      return [s.x, s.y, s.x + s.w, s.y + s.h];
    case 'circle':
      return [s.x - s.r, s.y - s.r, s.x + s.r, s.y + s.r];
    case 'photo':
      return [s.x, s.y, s.x + s.size, s.y + s.size];
    case 'text': {
      const p = textPlacement(s, fontWidth(fontFor(fonts, s.weight), s.text, s.size));
      const w = Math.max(p.width, s.width);
      return [p.left - 2, s.y - s.size * 1.2, p.left + w + 2, s.y + s.size * 0.4];
    }
    case 'path': {
      let x0 = Infinity;
      let y0 = Infinity;
      let x1 = -Infinity;
      let y1 = -Infinity;
      walkPath(s.d, (_, pts) => {
        for (let i = 0; i < pts.length; i += 2) {
          x0 = Math.min(x0, pts[i] + s.x);
          x1 = Math.max(x1, pts[i] + s.x);
          y0 = Math.min(y0, pts[i + 1] + s.y);
          y1 = Math.max(y1, pts[i + 1] + s.y);
        }
      });
      // Room for the line's own width.
      return [x0 - 3, y0 - 3, x1 + 3, y1 + 3];
    }
  }
}

export interface VectorOptions {
  title: string;
  /** The small line at each page's foot, if any. */
  footer?: (page: number, tile: Tile) => string | undefined;
  created?: Date;
}

/** The chart's shapes laid out on the plan's pages, as a vector PDF. */
export async function vectorPdf(chart: ChartSvg, plan: PagePlan, assets: VectorAssets, o: VectorOptions): Promise<Uint8Array> {
  const { fonts, photos, labels } = assets;
  const shapes = chart.shapes;
  const w = new PdfWriter();
  const catalog = w.reserve();
  const pagesId = w.reserve();
  const resources = w.reserve();

  // Fonts: the glyphs each face needs, and the characters they stand for (for copying text out).
  const used = { regular: new Map<number, string>(), semibold: new Map<number, string>() };
  shapes.forEach((s, i) => {
    if (s.kind !== 'text' || labels.has(i) || s.decorative) return;
    const face = s.weight >= BOLD ? 'semibold' : 'regular';
    for (const ch of s.text) {
      const g = glyphFor(fonts[face], ch);
      if (g && !used[face].has(g)) used[face].set(g, ch);
    }
  });
  const fontIds: Record<'regular' | 'semibold', number> = { regular: 0, semibold: 0 };
  const names = { regular: 'AAAAAA+NotoSans-Regular', semibold: 'AAAAAB+NotoSans-SemiBold' };
  for (const face of ['regular', 'semibold'] as const) {
    const glyphs = used[face];
    if (!glyphs.size) continue;
    const font = fonts[face];
    const sub = font.subset(glyphs.keys());
    const file = w.stream(`/Length1 ${sub.length} /Filter /FlateDecode`, await deflate(sub));
    const scale = 1000 / font.unitsPerEm;
    const descriptor = w.object(`<< /Type /FontDescriptor /FontName /${names[face]} /Flags 4 /FontBBox [-600 ${n(font.descent * scale)} 2800 ${n(font.ascent * scale)}] /ItalicAngle 0 /Ascent ${n(font.ascent * scale)} /Descent ${n(font.descent * scale)} /CapHeight 714 /StemV ${face === 'semibold' ? 120 : 80} /FontFile2 ${file} 0 R >>`);
    const widths = [...glyphs.keys()].sort((a, b) => a - b).map((g) => `${g} [${n(font.advance(g) * scale)}]`).join(' ');
    const cid = w.object(`<< /Type /Font /Subtype /CIDFontType2 /BaseFont /${names[face]} /CIDSystemInfo << /Registry (Adobe) /Ordering (Identity) /Supplement 0 >> /FontDescriptor ${descriptor} 0 R /DW 0 /W [${widths}] /CIDToGIDMap /Identity >>`);
    const unicode = w.stream('', toUnicodeCMap([...glyphs].map(([g, ch]) => [g, ch])));
    fontIds[face] = w.object(`<< /Type /Font /Subtype /Type0 /BaseFont /${names[face]} /Encoding /Identity-H /DescendantFonts [${cid} 0 R] /ToUnicode ${unicode} 0 R >>`);
  }

  // The blank font, for invisible text over labels drawn as pictures.
  const hidden: string[] = [];
  const hiddenCode = new Map<string, number>();
  for (const i of labels.keys()) {
    const s = shapes[i] as Extract<Shape, { kind: 'text' }>;
    if (s.decorative) continue;
    for (const ch of s.text) if (!hiddenCode.has(ch)) hiddenCode.set(ch, hidden.push(ch));
  }
  let blankId = 0;
  if (hidden.length) {
    const ttf = blankFont();
    const file = w.stream(`/Length1 ${ttf.length}`, ttf);
    const descriptor = w.object(`<< /Type /FontDescriptor /FontName /RelationshipsText /Flags 4 /FontBBox [0 -200 ${BLANK_ADVANCE} 800] /ItalicAngle 0 /Ascent 800 /Descent -200 /CapHeight 700 /StemV 80 /FontFile2 ${file} 0 R >>`);
    const map = new Uint8Array((hidden.length + 1) * 2);
    for (let i = 1; i <= hidden.length; i++) map[i * 2 + 1] = 1;
    const gidMap = w.stream('', map);
    const cid = w.object(`<< /Type /Font /Subtype /CIDFontType2 /BaseFont /RelationshipsText /CIDSystemInfo << /Registry (Adobe) /Ordering (Identity) /Supplement 0 >> /FontDescriptor ${descriptor} 0 R /DW ${BLANK_ADVANCE} /CIDToGIDMap ${gidMap} 0 R >>`);
    const unicode = w.stream('', toUnicodeCMap(hidden.map((ch, i) => [i + 1, ch])));
    blankId = w.object(`<< /Type /Font /Subtype /Type0 /BaseFont /RelationshipsText /Encoding /Identity-H /DescendantFonts [${cid} 0 R] /ToUnicode ${unicode} 0 R >>`);
  }
  const footerFont = w.object('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');

  // Pictures: photos (one object each, however often they appear) and labels.
  const photoNames = new Map<string, string>();
  const xobjects: string[] = [];
  for (const [href, im] of photos) {
    const name = `P${photoNames.size}`;
    photoNames.set(href, name);
    xobjects.push(`/${name} ${w.image(im)} 0 R`);
  }
  const labelNames = new Map<number, string>();
  for (const [i, l] of labels) {
    const name = `L${labelNames.size}`;
    labelNames.set(i, name);
    xobjects.push(`/${name} ${w.image(l.image)} 0 R`);
  }

  // See-through lines (former partners, irregular links) need a graphics state each.
  const alphas = new Map<number, string>();
  for (const s of shapes) if ('style' in s && s.style.opacity !== undefined && !alphas.has(s.style.opacity)) alphas.set(s.style.opacity, `A${alphas.size}`);
  const states = [...alphas].map(([a, name]) => `/${name} << /Type /ExtGState /CA ${n(a)} /ca ${n(a)} >>`);

  const fontRes = [`/F0 ${footerFont} 0 R`, fontIds.regular ? `/F1 ${fontIds.regular} 0 R` : '', fontIds.semibold ? `/F2 ${fontIds.semibold} 0 R` : '', blankId ? `/T0 ${blankId} 0 R` : ''].filter(Boolean).join(' ');
  w.object(`<< /Font << ${fontRes} >> /XObject << ${xobjects.join(' ')} >> /ExtGState << ${states.join(' ')} >> >>`, resources);

  const paintOps = (st: Style): { pre: string; op: string } => {
    const fill = st.fill && st.fill !== 'none' ? rgb(st.fill) : undefined;
    const stroke = st.stroke ? rgb(st.stroke) : undefined;
    const pre = [
      fill ? `${fill} rg` : '',
      stroke ? `${stroke} RG ${n(st.width ?? 1)} w` : '',
      st.dash ? `[${st.dash}] 0 d` : '',
      st.cap === 'round' ? '1 J' : '',
      st.opacity !== undefined ? `/${alphas.get(st.opacity)} gs` : '',
    ]
      .filter(Boolean)
      .join(' ');
    return { pre, op: fill && stroke ? 'B' : fill ? 'f' : stroke ? 'S' : 'n' };
  };

  const shapeOps = (s: Shape, i: number): string => {
    switch (s.kind) {
      case 'rect': {
        const p = paintOps(s.style);
        return `q ${p.pre} ${roundRect(s.x, s.y, s.w, s.h, s.r)} ${p.op} Q`;
      }
      case 'circle': {
        const p = paintOps(s.style);
        return `q ${p.pre} ${circlePath(s.x, s.y, s.r)} ${p.op} Q`;
      }
      case 'path': {
        const p = paintOps(s.style);
        return `q ${p.pre} ${pathOps(s.d, s.x, s.y)} ${p.op} Q`;
      }
      case 'photo': {
        const name = photoNames.get(s.href);
        if (!name) return '';
        const r = s.size / 2;
        // In this flipped space an image's unit square is drawn upside down, so it's flipped back.
        return `q ${circlePath(s.x + r, s.y + r, r)} W n ${n(s.size)} 0 0 ${n(-s.size)} ${n(s.x)} ${n(s.y + s.size)} cm /${name} Do Q`;
      }
      case 'text': {
        const pic = labels.get(i);
        if (pic) {
          const draw = `q ${n(pic.w)} 0 0 ${n(-pic.h)} ${n(pic.x)} ${n(pic.y + pic.h)} cm /${labelNames.get(i)} Do Q`;
          if (s.decorative) return draw;
          const codes = [...s.text].map((ch) => code4(hiddenCode.get(ch)!)).join('');
          const glyphs = [...s.text].length;
          const left = s.middle ? s.x - s.width / 2 : s.x;
          const stretch = (s.width / Math.max(1, glyphs * (BLANK_ADVANCE / 1000) * s.size)) * 100;
          // The invisible mode (3 Tr) is part of the graphics state, so it's kept inside q…Q.
          return `${draw} q BT 3 Tr /T0 ${n(s.size)} Tf ${n(stretch)} Tz 1 0 0 -1 ${n(left)} ${n(s.y)} Tm <${codes}> Tj ET Q`;
        }
        const face = s.weight >= BOLD ? 'semibold' : 'regular';
        const f = fonts[face];
        const p = textPlacement(s, fontWidth(f, s.text, s.size));
        const gids = [...s.text].map((ch) => code4(glyphFor(f, ch))).join('');
        // Text matrices flip y back, so letters stand upright in this y-down space.
        if (s.decorative) {
          // Initials on avatars are drawn as the letters' outlines, not text, so copying a name gets just the name.
          const scale = s.size / f.unitsPerEm;
          let x = p.left;
          const letters: string[] = [];
          for (const ch of s.text) {
            const g = glyphFor(f, ch);
            letters.push(`q ${fine(scale * (p.stretch / 100))} 0 0 ${fine(-scale)} ${n(x)} ${n(s.y)} cm ${f.outline(g)} f Q`);
            x += f.advance(g) * scale * (p.stretch / 100);
          }
          return `q ${rgb(s.color) ?? '0 0 0'} rg ${letters.join(' ')} Q`;
        }
        return `BT 0 Tr ${rgb(s.color) ?? '0 0 0'} rg /${face === 'semibold' ? 'F2' : 'F1'} ${n(s.size)} Tf ${n(p.stretch)} Tz 1 0 0 -1 ${n(p.left)} ${n(s.y)} Tm <${gids}> Tj ET`;
      }
    }
  };

  const boxes = shapes.map((s) => bounds(s, fonts));
  const pageIds: number[] = [];
  for (const [pi, t] of plan.tiles.entries()) {
    const ops: string[] = [];
    // Clip to the page's share of the chart, then draw in the chart's own pixels, y down.
    ops.push(`q ${n(t.x)} ${n(t.y)} ${n(t.w)} ${n(t.h)} re W n ${n(t.k)} 0 0 ${n(-t.k)} ${n(t.x - t.sx * t.k)} ${n(t.y + t.h + t.sy * t.k)} cm 1 j`);
    shapes.forEach((s, i) => {
      const [x0, y0, x1, y1] = boxes[i];
      if (x1 < t.sx || x0 > t.sx + t.sw || y1 < t.sy || y0 > t.sy + t.sh) return;
      const op = shapeOps(s, i);
      if (op) ops.push(op);
    });
    ops.push('Q');
    const footer = o.footer?.(pi, t);
    if (footer) ops.push(`BT /F0 8 Tf 0.45 g ${n((plan.width - approxWidth(footer, 8)) / 2)} ${n(Math.min(18, t.y / 2))} Td (${winAnsi(footer)}) Tj ET`);
    const contents = w.stream('/Filter /FlateDecode', await deflate(new TextEncoder().encode(ops.join('\n'))));
    pageIds.push(w.object(`<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${n(plan.width)} ${n(plan.height)}] /Resources ${resources} 0 R /Contents ${contents} 0 R >>`));
  }
  w.object(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`, pagesId);
  w.object(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`, catalog);
  const info = w.object(`<< /Title ${pdfText(o.title)} /Producer (Relationships) /CreationDate (${pdfDate(o.created ?? new Date())}) >>`);
  return w.finish(catalog, info);
}

// ---------------------------------------------------------------- in the browser

let fontsLoading: Promise<VectorFonts> | undefined;

/** Noto Sans, from the app's own files (fetched the first time a PDF is made). */
export function loadFonts(): Promise<VectorFonts> {
  if (!fontsLoading) {
    const get = async (file: string) => {
      const res = await fetch(new URL(`fonts/${file}`, document.baseURI));
      if (!res.ok) throw new Error(`Couldn’t load the ${file} font.`);
      return parseFont(new Uint8Array(await res.arrayBuffer()));
    };
    fontsLoading = Promise.all([get('NotoSans-Regular.ttf'), get('NotoSans-SemiBold.ttf')]).then(([regular, semibold]) => ({ regular, semibold }));
    fontsLoading.catch(() => (fontsLoading = undefined));
  }
  return fontsLoading;
}

/** How much sharper than the chart's own pixels a label drawn as a picture is (about 600 dots per inch at full size). */
const LABEL_SCALE = 6;

/** Photos as small square JPEGs, and labels the fonts can't set as transparent pictures. */
export async function prepareAssets(chart: ChartSvg, fonts: VectorFonts): Promise<VectorAssets> {
  const photos = new Map<string, PdfImage>();
  for (const s of chart.shapes) {
    if (s.kind !== 'photo' || photos.has(s.href)) continue;
    try {
      const img = new Image();
      img.src = s.href;
      await img.decode();
      // Cropped to a square from the middle, as the chart shows it.
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      const out = Math.min(256, side);
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = out;
      canvas.getContext('2d')!.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, out, out);
      const jpeg = new Uint8Array(await (await canvasBlob(canvas, 'image/jpeg', 0.9)).arrayBuffer());
      photos.set(s.href, jpegImage(jpeg));
    } catch {
      /* a photo that won't load is left out; the card still shows */
    }
  }
  const labels = new Map<number, LabelPicture>();
  for (const [i, s] of chart.shapes.entries()) {
    if (s.kind !== 'text' || !needsPicture(fonts, s)) continue;
    const pad = 2;
    const left = s.middle ? s.x - s.width / 2 : s.x;
    const w = s.width + pad * 2;
    const h = s.size * 1.6;
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(w * LABEL_SCALE);
    canvas.height = Math.ceil(h * LABEL_SCALE);
    const ctx = canvas.getContext('2d')!;
    ctx.scale(LABEL_SCALE, LABEL_SCALE);
    ctx.font = cssFont(s.size, s.weight);
    ctx.fillStyle = s.color;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(s.text, pad, s.size * 1.2);
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    labels.set(i, { image: await flateImage(pixels, canvas.width, canvas.height, true), x: left - pad, y: s.y - s.size * 1.2, w, h });
  }
  return { fonts, photos, labels };
}
